'use client'

import { useTranslations } from 'next-intl'
import { Fragment, type MouseEvent } from 'react'
import AppLink from '@/components/elementary/AppLink'
import { Check } from '@/components/icons/Check'
import { Dropdown } from '@/components/icons/Dropdown'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { LOCALE_TRANSLATION } from '@/constants/i18n.constants'
import { cn } from '@/lib/styles'
import type { AppLocale } from '@/types/general'
import type { LocaleLink } from '@/types/i18n.types'

type Props = {
	currentLocale: AppLocale
	localeLinks: LocaleLink[]
	onLocaleChange?: (locale: AppLocale) => void
}

const LocaleSwitcher = ({ currentLocale, localeLinks, onLocaleChange }: Props) => {
	const t = useTranslations('LocaleSwitcher')
	// Controlled mode: when a parent manages locale state, prevent navigation and delegate the update.
	const handleLocaleLinkClick = (locale: AppLocale) => (event: MouseEvent<HTMLAnchorElement>) => {
		if (!onLocaleChange) return
		event.preventDefault()
		onLocaleChange(locale)
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					type="button"
					variant="none"
					size="none"
					aria-label={t('selectLanguage')}
					className="h-9 w-16.5 justify-between rounded-sm border border-gray-400 bg-neutral-light-100 px-2 py-2.5 lok-text-body-bold-medium uppercase"
				>
					<span>{currentLocale}</span>
					<Dropdown className="size-5" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				className="rounded-sm border-none shadow-box"
				side="bottom"
				align="end"
				sideOffset={18}
			>
				{localeLinks.map((link, index) => {
					const isActive = link.locale === currentLocale

					return (
						<Fragment key={link.locale}>
							<DropdownMenuItem asChild className="p-0">
								<AppLink
									href={link.pathname}
									locale={link.locale}
									variant="none"
									size="none"
									lang={link.locale}
									aria-current={isActive ? 'true' : undefined}
									className={cn(
										'relative w-full rounded-sm px-2 py-1.5 pr-8',
										'hover:bg-accent focus:bg-accent focus:text-accent-foreground cursor-pointer',
										isActive ? 'lok-text-body-bold-medium' : 'lok-text-body-regular-medium',
									)}
									onClick={handleLocaleLinkClick(link.locale)}
								>
									<span>{LOCALE_TRANSLATION[link.locale as keyof typeof LOCALE_TRANSLATION]}</span>
									{isActive && (
										<span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
											<Check className="size-3" />
										</span>
									)}
								</AppLink>
							</DropdownMenuItem>
							{index < localeLinks.length - 1 && <DropdownMenuSeparator />}
						</Fragment>
					)
				})}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

export default LocaleSwitcher
